import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { StockCarType } from '../../types/oneGraphTypes';
import { VehicleLocationWrapper, Container, VehicleDetailsPageWrapper } from './LocationBar.styles';
import { Text, TextSkeleton } from '@oneaudi/unified-web-components';

//* included for temporary fix related to stock number being wrong on us market vehicles
interface VehicleLocationTypes {
  vehicle: StockCarType | null;
  isCanadian: boolean;
  isTier3: boolean;
}

const VehicleLocation: React.FC<VehicleLocationTypes> = ({ vehicle, isCanadian, isTier3 }) => {
  const { t } = useTranslation();
  const salesInfo = vehicle?.salesInfo;
  const dynamicAttributes = vehicle?.dynamicAttributes;
  const isAvailableNow = salesInfo?.availableFromDateInfo?.value === 'Immediately';
  const stockNumber = vehicle?.commissionNumber;
  // OADD Stock Number
  const oaddStockNumber = useMemo(() => {
    const stockNumberObject = dynamicAttributes?.find((key) => key.id === 'VEHICLE_ID');
    if (stockNumberObject) {
      return stockNumberObject.value;
    }
    return null;
  }, [dynamicAttributes]);

  const dealerName = vehicle?.dealer.name;
  const kvpsSyncId = vehicle?.dealer.id;

  const getStockNumberLabel = useMemo(() => {
    if (!isAvailableNow && !stockNumber) return null;

    if (isAvailableNow) {
      return t('locationBar.stock', { stockNumber: stockNumber });
    }

    return t('locationBar.inventory', { stockNumber: stockNumber });
  }, [isAvailableNow, stockNumber, t]);

  const DivTextSkeleton = () => (
    <div
      style={{
        width: 200,
      }}
    >
      <TextSkeleton
        className="className"
        data-testid="test-id"
        emphasis="normal"
        lines={1}
        order="1"
      />
    </div>
  );

  return (
    <VehicleLocationWrapper>
      <VehicleDetailsPageWrapper data-testid="vehicle-location-wrapper">
        <Container>
          {dealerName ? (
            <Text
              data-vehicle-dealer-name={dealerName}
              data-vehicle-dealer-code={kvpsSyncId}
              data-testid="vehicle-location"
            >
              {dealerName}
            </Text>
          ) : (
            <DivTextSkeleton />
          )}

          {isCanadian &&
            !isTier3 &&
            (stockNumber ? (
              <Text data-vehicle-stock-number={stockNumber} data-testid="vehicle-stock-number">
                {getStockNumberLabel}
              </Text>
            ) : (
              <DivTextSkeleton />
            ))}
          {isTier3 && oaddStockNumber ? (
            <Text data-vehicle-stock-number={oaddStockNumber} data-testid="vehicle-stock-number">
              {t('locationBar.stock', { stockNumber: oaddStockNumber })}
            </Text>
          ) : null}
        </Container>
      </VehicleDetailsPageWrapper>
    </VehicleLocationWrapper>
  );
};

export default VehicleLocation;
